// src/components/workflows/nodes/ScheduleTriggerNode.jsx - Schedule Trigger Node for Client Automations
import React from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { Badge } from '../../ui/badge.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select.tsx';
import { Input } from '../../ui/input.tsx';
import { Switch } from '../../ui/switch.tsx';
import { Label } from '../../ui/label.tsx';
import { Clock, Calendar, Timer } from 'lucide-react';

/**
 * ScheduleTriggerNode - Entry point for scheduled automations
 *
 * Node data structure:
 * {
 *   type: 'scheduleTrigger',
 *   data: {
 *     label: 'Run Daily',
 *     schedule: {
 *       enabled: true,
 *       type: 'daily' | 'interval' | 'weekly',
 *       time: '08:00',
 *       timezone: 'America/Chicago',
 *       interval_value: 4,
 *       interval_unit: 'hours',
 *       day_of_week: 1
 *     }
 *   }
 * }
 */
const ScheduleTriggerNode = ({ data, selected, id }) => {
  const {
    label = 'Schedule',
    schedule = {}
  } = data;

  const {
    enabled = false,
    type = 'daily',
    time = '08:00',
    timezone = 'America/Chicago',
    interval_value = 4,
    interval_unit = 'hours',
    day_of_week = 1
  } = schedule;

  const { updateNodeData } = useReactFlow();

  // Update schedule config
  const updateSchedule = (updates) => {
    updateNodeData(id, {
      ...data,
      schedule: { ...schedule, ...updates }
    });
  };

  // Get human-readable schedule description
  const getScheduleDescription = () => {
    if (!enabled) return 'Paused';

    switch (type) {
      case 'daily':
        return `Daily at ${formatTime(time)}`;
      case 'interval':
        return `Every ${interval_value} ${interval_unit}`;
      case 'weekly':
        return `${getDayName(day_of_week)} at ${formatTime(time)}`;
      default:
        return 'Not configured';
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const getDayName = (day) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[day] || 'Monday';
  };

  const TIMEZONES = [
    { value: 'America/New_York', label: 'Eastern (ET)' },
    { value: 'America/Chicago', label: 'Central (CT)' },
    { value: 'America/Denver', label: 'Mountain (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  ];

  return (
    <>
      {/* No input handle - this is the start */}

      <div className={`
        w-64 rounded-lg border bg-slate-900
        ${selected ? 'ring-2 ring-green-500 shadow-lg border-green-500/50' : 'shadow-md border-green-500/20'}
        transition-all duration-200 hover:shadow-xl
        bg-gradient-to-br from-green-500/10 to-green-600/5
      `}>
        {/* Header */}
        <div className="p-3 pb-2 border-b border-slate-700/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-green-400" />
              <div className="text-base font-semibold text-green-100">
                {label}
              </div>
            </div>
            <Badge
              variant="outline"
              className={`text-xs ${enabled ? 'border-green-500/50 text-green-300' : 'border-slate-500/50 text-slate-400'}`}
            >
              {enabled ? 'Active' : 'Paused'}
            </Badge>
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {getScheduleDescription()}
          </div>
        </div>

        {/* Content */}
        <div className="p-3 pt-2 space-y-3">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between nodrag">
            <Label className="text-xs text-slate-300">Enabled</Label>
            <Switch
              checked={enabled}
              onCheckedChange={(checked) => updateSchedule({ enabled: checked })}
            />
          </div>

          {/* Schedule Type */}
          <div className="space-y-1 nodrag">
            <Label className="text-xs text-slate-400">Frequency</Label>
            <Select value={type} onValueChange={(value) => updateSchedule({ type: value })}>
              <SelectTrigger className="h-8 text-xs bg-slate-800 border-slate-700 text-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="daily" className="text-slate-200">Daily</SelectItem>
                <SelectItem value="interval" className="text-slate-200">Interval</SelectItem>
                <SelectItem value="weekly" className="text-slate-200">Weekly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Time (for daily/weekly) */}
          {(type === 'daily' || type === 'weekly') && (
            <div className="space-y-1 nodrag">
              <Label className="text-xs text-slate-400">Time</Label>
              <Input
                type="time"
                value={time}
                onChange={(e) => updateSchedule({ time: e.target.value })}
                className="h-8 text-xs bg-slate-800 border-slate-700 text-slate-200"
              />
            </div>
          )}

          {/* Day of Week (for weekly) */}
          {type === 'weekly' && (
            <div className="space-y-1 nodrag">
              <Label className="text-xs text-slate-400">Day</Label>
              <Select
                value={String(day_of_week)}
                onValueChange={(value) => updateSchedule({ day_of_week: parseInt(value) })}
              >
                <SelectTrigger className="h-8 text-xs bg-slate-800 border-slate-700 text-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, i) => (
                    <SelectItem key={i} value={String(i)} className="text-slate-200">{day}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Interval config */}
          {type === 'interval' && (
            <div className="flex gap-2 nodrag">
              <div className="flex-1 space-y-1">
                <Label className="text-xs text-slate-400">Every</Label>
                <Input
                  type="number"
                  min="1"
                  value={interval_value}
                  onChange={(e) => updateSchedule({ interval_value: parseInt(e.target.value) || 1 })}
                  className="h-8 text-xs bg-slate-800 border-slate-700 text-slate-200"
                />
              </div>
              <div className="flex-1 space-y-1">
                <Label className="text-xs text-slate-400">Unit</Label>
                <Select value={interval_unit} onValueChange={(value) => updateSchedule({ interval_unit: value })}>
                  <SelectTrigger className="h-8 text-xs bg-slate-800 border-slate-700 text-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="minutes" className="text-slate-200">Minutes</SelectItem>
                    <SelectItem value="hours" className="text-slate-200">Hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Timezone */}
          <div className="space-y-1 nodrag">
            <Label className="text-xs text-slate-400">Timezone</Label>
            <Select value={timezone} onValueChange={(value) => updateSchedule({ timezone: value })}>
              <SelectTrigger className="h-8 text-xs bg-slate-800 border-slate-700 text-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {TIMEZONES.map(tz => (
                  <SelectItem key={tz.value} value={tz.value} className="text-slate-200">
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="out"
        style={{ right: '-8px', zIndex: 1000 }}
        className="w-3 h-3 !bg-green-500"
      />
    </>
  );
};

export default ScheduleTriggerNode;
